import {
  Flex,
  FlexProps,
  Grid,
  GridItem,
  Link,
  Progress,
} from "@chakra-ui/react";
import React from "react";
import { Link as RouterLink } from "react-router";

import CompactNumber from "@/common/components/CompactNumber";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import PercentageNumber from "@/common/components/PercentageNumber";
import {
  SectionHeading,
  SectionHeadingProps,
} from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFeatureFeedbackHistogramData } from "@/feature/data/useFeatureFeedbackHistogramData";
import {
  FeedbackSatisfactionIcon,
  useSatisfactionColors,
} from "@/feedback/components/FeedbackSatisfaction";
import {
  SatisfactionScore,
  SatisfactionSpan,
} from "@reflag/shared/schemas/satisfactionScore";
import { FeedbackUrl } from "@reflag/shared/urls";
import { getFraction } from "@reflag/shared/utils/getFraction";

const scoreOrder: Array<SatisfactionScore> = [5, 4, 3, 2, 1];

type Props = Omit<FlexProps, "title"> & {
  featureId: string;
  subsegmentId?: string;
  title?: string | null;
  _sectionHeading?: SectionHeadingProps;
};

export default function SatisfactionHistogram({
  featureId,
  subsegmentId,
  title = "Satisfaction",
  _sectionHeading,
  ...rest
}: Props) {
  const { env } = useEnvironment();
  const satisfactionColors = useSatisfactionColors();
  const { data, isLoading, isSuccess } = useFeatureFeedbackHistogramData(
    featureId,
    {
      subsegment: subsegmentId,
    },
  );

  return (
    <Flex direction="column" gap={6} {...rest}>
      <SectionHeading
        alignItems="center"
        display="flex"
        gap={2}
        isLoading={isLoading}
        {..._sectionHeading}
      >
        {title}
        <InfoIconTooltip text="Satisfaction distribution is not affected by the time period." />
      </SectionHeading>
      <Grid
        alignItems="center"
        fontSize="sm"
        gap={3}
        maxW="xs"
        rowGap={4}
        templateColumns="min-content 1fr min-content min-content"
      >
        {scoreOrder.map((score) => {
          const scoreIndex = score === 0 ? "noScore" : score;
          const scoreFraction = data
            ? getFraction(data[scoreIndex], data.total)
            : 0;

          return (
            <React.Fragment key={`sat-${scoreIndex}`}>
              <FeedbackSatisfactionIcon colored={true} value={score} />

              <Progress
                color={satisfactionColors[score]}
                height="6px"
                max={1}
                min={0}
                value={scoreFraction}
              />
              <GridItem textAlign="right">
                <PercentageNumber value={scoreFraction} />
              </GridItem>

              {!isSuccess ? (
                <NotAvailableCell />
              ) : (
                <Link
                  as={RouterLink}
                  color="dimmed"
                  to={FeedbackUrl(env, {
                    feature: featureId,
                    satisfaction: satisfactionScoreToSpan(score),
                    segment: subsegmentId,
                  })}
                >
                  <CompactNumber value={data[scoreIndex]} />
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </Grid>
    </Flex>
  );
}

function satisfactionScoreToSpan(score: SatisfactionScore): SatisfactionSpan {
  switch (score) {
    case 1:
    case 2:
      return "unsatisfied";
    case 3:
      return "neutral";
    case 4:
    case 5:
      return "satisfied";
    default:
      return "neutral";
  }
}
